import { Box, Stack } from '@mui/material'
import { ReactComponent as CrossCloseLogo } from 'assets/cross_close_logo.svg'
import { useWidgetFoodtable } from 'components/farm-dashboard/hooks/useWidgetFoodtable'
import { TABS_GROUP_PAGE } from 'components/group/GroupSpecific'
import { tooltipForDashboardChart } from 'components/group/utils/tooltipsForCharts'
import { useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from 'store/hooks'
import { COLORS } from 'styles/colors'
import { ChipItem } from 'ui/ChipItem'
import Preloader from 'ui/Preloader'
import { TypographySecondary14 } from 'ui/typography/TypographySecondary14'

const MAX_PERCENT_FOR_VIEW = 100

const FoodTableChart = () => {
	const [activeBarnButton, setActiveBarnButton] = useState('all')
	const { groupList, loading } = useAppSelector(state => state.groupsReducer)
	const navigate = useNavigate()
	const { foodtableInfo, initialFoodtableInfo, isLoading, setFoodtableInfo } =
		useWidgetFoodtable()

	const emptyData = []
	const cameraIssueData = []
	const reachableData = []
	const unreachableData = []

	foodtableInfo?.forEach(item => {
		item.food_info.forEach(info => {
			const isCameraIssue = info.camera_issue
			const isEmpty = info.reachable_percent <= 2 && !isCameraIssue

			cameraIssueData.push(isCameraIssue ? MAX_PERCENT_FOR_VIEW : 0)
			emptyData.push(isEmpty ? MAX_PERCENT_FOR_VIEW : 0)

			reachableData.push(
				!isCameraIssue && !isEmpty ? info.reachable_percent : 0,
			)
			unreachableData.push(
				!isCameraIssue && !isEmpty ? info.unreachable_percent : 0,
			)
		})
	})
	const totalData = foodtableInfo?.flatMap(item =>
		item.food_info.map(info => info.total_percent),
	)
	const labelData = foodtableInfo?.flatMap(item =>
		item.food_info.map(info => info.group_title),
	)
	const series = [
		{
			name: 'Нет данных',
			color: COLORS.icon,
			data: cameraIssueData,
		},
		{
			name: 'Нет корма',
			color: COLORS.error,
			data: emptyData,
		},
		{
			name: 'Недоступно',
			color: COLORS.health,
			data: unreachableData,
		},
		{
			name: 'Доступно',
			color: COLORS.success,
			data: reachableData,
		},
	]
	const seriesLength = cameraIssueData.length
	const minWidthForOneColumn = 5
	const maxWidthForManyColumns = 60
	const columnWidth =
		minWidthForOneColumn +
		maxWidthForManyColumns / (1 + 30 * Math.exp(-seriesLength / 3))

	const options: any = {
		mask: false,
		chart: {
			events: {
				click: function (event, chartContext, opts) {
					const activeIndex = opts.dataPointIndex
					const groupTitle = opts.globals.categoryLabels[activeIndex]
					const groupKey = groupList.find(
						group => group.title === groupTitle,
					).key
					navigate(`/feed/groups/${groupKey}?tab=${TABS_GROUP_PAGE.FOOD_TABLE}`)
				},
				dataPointMouseEnter: function (event) {
					event.target.style.cursor = 'pointer'
				},
			},
			animations: {
				enabled: false,
				speed: 300,
				animateGradually: {
					enabled: false,
					delay: 0,
				},
				dynamicAnimation: {
					enabled: true,
					speed: 300,
				},
			},
			zoom: {
				enabled: false,
			},
			stacked: true,
			height: 160,
			type: 'bar',
			toolbar: {
				show: false,
			},
		},
		dataLabels: {
			enabled: false,
		},
		grid: {
			padding: {
				left: 0,
				right: 0,
			},
			show: true,
			position: 'back',
			xaxis: {
				lines: {
					show: true,
				},
			},
		},
		plotOptions: {
			bar: {
				horizontal: false,
				borderRadius: 8,
				borderRadiusApplication: 'end', // 'around', 'end'
				borderRadiusWhenStacked: 'last', // 'all', 'last'
				columnWidth: `${columnWidth}%`,
				dataLabels: {
					hideOverflowingLabels: true,
				},
			},
		},
		fill: {
			opacity: 1,
		},
		xaxis: {
			crosshairs: {
				show: false,
			},
			type: 'category',
			tickPlacement: 'on',
			tickAmount: 31,
			tooltip: {
				enabled: false,
			},
			title: {
				text: 'Группы по коровникам',
				style: {
					color: COLORS.icon,
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontWeight: 400,
				},
			},
			labels: {
				rotate: 0,
				trim: false,
				hideOverlappingLabels: false,
				datetimeUTC: false,
				style: {
					background: COLORS.icon,
					padding: '2px 4px',
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					colors: [COLORS.black],
				},
			},
			categories: labelData,
		},
		yaxis: {
			crosshairs: {
				show: false,
			},
			tickAmount: 2,
			max: 100,
			min: 0,
			labels: {
				style: {
					fontSize: '12px',
					fontFamily: 'Montserrat',
					fontStyle: 'normal',
					fontWeight: 400,
					lineHeight: '15px',
					color: '#7F7F84',
				},
				formatter: function (value: number) {
					if (value === 0) {
						return value
					} else {
						return value + '%'
					}
				},
			},
		},
		states: {
			normal: {
				filter: {
					type: 'none',
					value: 0,
				},
			},
			hover: {
				filter: {
					type: 'lighten',
				},
			},
			active: {
				allowMultipleDataPointsSelection: false,
				filter: {
					type: 'none',
					value: 0,
				},
			},
		},
		tooltip: {
			enable: true,
			intersect: true,
			shared: false,
			custom: ({ dataPointIndex }) => {
				return tooltipForDashboardChart(
					labelData,
					dataPointIndex,
					reachableData,
					unreachableData,
					totalData,
					emptyData,
					cameraIssueData,
				)
			},
		},
		legend: {
			show: false,
		},
	}
	const handleActiveBarn = barnName => {
		setActiveBarnButton(barnName)
		filterChartData(barnName)
	}

	const handleDeleteBarn = () => {
		setActiveBarnButton('all')
		setFoodtableInfo(initialFoodtableInfo)
	}
	const filterChartData = barnName => {
		const filteredData = initialFoodtableInfo.filter(
			item => item.barn_title === barnName,
		)
		setFoodtableInfo(filteredData)
	}

	return (
		<>
			{isLoading || loading ? (
				<div
					style={{ height: '210px', display: 'flex', justifyContent: 'center' }}
				>
					<Preloader />
				</div>
			) : foodtableInfo.length === 0 ? (
				<Box
					sx={{
						height: '255px',
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'center',
						alignItems: 'center',
						gap: '4px',
					}}
				>
					<TypographySecondary14>Нет данных</TypographySecondary14>
					<TypographySecondary14>
						Нет данных, соответствующих заданным условиям
					</TypographySecondary14>
				</Box>
			) : (
				<>
					<Stack direction='row' gap='8px' ml='33px' mt='16px'>
						{initialFoodtableInfo.map((el, index) => (
							<ChipItem
								onClick={() => handleActiveBarn(el.barn_title)}
								deleteIcon={
									activeBarnButton === el.barn_title ? (
										<CrossCloseLogo
											style={{ stroke: 'white', width: '16px', height: '16px' }}
										/>
									) : (
										<></>
									)
								}
								onDelete={() => handleDeleteBarn()}
								label={el.barn_title}
								key={index}
								sx={{
									height: '24px',
									background:
										activeBarnButton === el.barn_title
											? COLORS.main
											: COLORS.inputBorder,
									color: activeBarnButton === el.barn_title ? '#fff' : '#000',
								}}
							/>
						))}
					</Stack>
					<div style={{ marginLeft: '-20px' }} id={'dashboardChart'}>
						<ReactApexChart
							options={options}
							series={series}
							type='bar'
							height={155}
							className={'apex-chart'}
						/>
					</div>
				</>
			)}
		</>
	)
}

export default FoodTableChart
